<script setup lang="ts">
import { computed } from 'vue';
import { useAbility } from '@casl/vue';

import TableAlert from '@/components/alerts/TableAlert.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import PostCodeSearchSearchForm from '@/components/searchForms/PostCodeSearchSearchForm.vue';
import PostCodeSearchTable from '@/components/tables/PostCodeSearchTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import usePostCodeSearchViewData from '@/composables/data/usePostCodeSearchViewData';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useDownloadCsv from '@/composables/useDownloadCsv';
import { useJobEditDialog } from '@/composables/useJobEditDialog';
import usePostCodeSearchView from '@/composables/views/usePostCodeSearchView';

const ability = useAbility();
const isStaff = computed(() => ability.can('staff', 'Job'));

const { getLastSevenDaysRange } = useCurrentDateString();
const { querySuburbId, queryPostCode, queryDistance, queryDates } =
  useCommonRouteQuery({
    queryDistance: 20,
    queryDates: getLastSevenDaysRange(),
  });
const { isDownloadingCSV, downloadCsv } = useDownloadCsv();
const { isFetchingPostCodeJobs, postCodeJobs, refreshPostCodeJobs } =
  usePostCodeSearchViewData(
    querySuburbId,
    queryPostCode,
    queryDistance,
    queryDates,
  );
const { search, initialData, total, results, resultsForCsv } =
  usePostCodeSearchView(
    querySuburbId,
    queryPostCode,
    queryDistance,
    queryDates,
    isStaff,
    isFetchingPostCodeJobs,
    postCodeJobs,
  );

const jobEditDialog = useJobEditDialog();

function handleEditClick(jobId: number, jobDate: string): void {
  jobEditDialog.open(jobId, jobDate, results.value, refreshPostCodeJobs);
}
</script>

<template>
  <div :class="$style.postCodeSearchView" data-testid="postCodeSearchView">
    <PageContentWrapper class="mt-2">
      <FormWrapper :class="$style.formWrapper" class="mt-0">
        <PostCodeSearchSearchForm
          :initial-data="initialData"
          :loading="isFetchingPostCodeJobs"
          :class="$style.form"
          @submit="search"
        />
      </FormWrapper>
      <TableAlert v-if="total === 200" :class="$style.tableAlert">
        Only 200 jobs are shown. Please, specify your searching request
      </TableAlert>
      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <PostCodeSearchTable
          :loading="isFetchingPostCodeJobs"
          :data="results"
          :is-staff="isStaff"
          @edit-click="handleEditClick"
        />
        <TablePageFooter v-if="results?.length" :total="total" no-pages>
          <ExportAsCsvIconBtn
            :disabled="isFetchingPostCodeJobs || !results?.length"
            :loading="isDownloadingCSV"
            @click="downloadCsv(resultsForCsv, 'postCodeSearch')"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.postCodeSearchView {
}
  .formWrapper {
    padding: 2.4rem 1.5rem 0.5rem 1.5rem;
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
    margin: 2rem 0;
  }
  .tablePageShadowWrapper {
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
  }
  .tableAlert {
    margin-bottom: 14px;
  }
</style>
